import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { SEO } from "@/components/SEO";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, BookOpen, Building2, Settings, Loader2, Map, LayoutGrid, FileText, Clock, Tag, X, Layers, ShieldX, Pencil, Sparkles, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserMenu } from "@/components/UserMenu";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { AIUploadWizard } from "@/components/creator/AIUploadWizard";
import { AIUploadJobList } from "@/components/creator/AIUploadJobList";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { trackAiUpload } from "@/lib/aiUploadAnalytics";
import { organizationsApi } from "@/lib/api/organizationsApi";

const CREATOR_ALLOWED_USER_ID = import.meta.env.VITE_CREATOR_ALLOWED_USER_ID || "50060165";
const AI_UPLOAD_ENABLED = import.meta.env.VITE_AI_UPLOAD_ENABLED !== 'false';

interface CreatedContent {
  pathways: Pathway[];
  blocks: Block[];
  modules: Module[];
  lessons: Lesson[];
}

interface Pathway {
  id: string;
  title: string;
  description: string | null;
  short_description: string | null;
  type: string;
  difficulty: string;
  status: string;
  tags: string[] | null;
  estimated_total_time: number | null;
  created_at: string;
}

interface Block {
  id: string;
  title: string;
  description: string | null;
  short_description: string | null;
  topic: string | null;
  difficulty: string;
  status: string;
  tags: string[] | null;
  estimated_time: number | null;
  created_at: string;
}

interface Lesson {
  id: string;
  title: string;
  short_summary: string | null;
  detailed_description: string | null;
  difficulty: string;
  status: string;
  estimated_time: number | null;
  created_at: string;
}

interface Module {
  id: string;
  title: string;
  description: string | null;
  short_description: string | null;
  status: string;
  tags: string[] | null;
  estimated_time: number | null;
  created_at: string;
}

const optionalNumber = z.preprocess(
  (val) => (val === "" || val === undefined || val === null ? undefined : Number(val)),
  z.number().min(1).optional()
);

const pathwayFormSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters").max(255, "Title must be less than 255 characters"),
  description: z.string().optional(),
  shortDescription: z.string().max(500, "Short description must be less than 500 characters").optional(),
  type: z.enum(["job_role", "skill", "subject", "quality"]).default("skill"),
  difficulty: z.enum(["beginner", "intermediate", "advanced", "expert"]).default("beginner"),
  estimatedTotalTime: optionalNumber,
  tags: z.string().optional(),
});

const blockFormSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters").max(255, "Title must be less than 255 characters"),
  description: z.string().optional(),
  shortDescription: z.string().max(500, "Short description must be less than 500 characters").optional(),
  topic: z.string().max(255).optional(),
  difficulty: z.enum(["beginner", "intermediate", "advanced", "expert"]).default("beginner"),
  estimatedTime: optionalNumber,
  tags: z.string().optional(),
});

const lessonFormSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters").max(255, "Title must be less than 255 characters"),
  shortSummary: z.string().max(500, "Summary must be less than 500 characters").optional(),
  detailedDescription: z.string().optional(),
  difficulty: z.enum(["beginner", "intermediate", "advanced", "expert"]).default("beginner"),
  estimatedTime: z.preprocess(
    (val) => (val === "" || val === undefined || val === null ? 10 : Number(val)),
    z.number().min(1).max(60)
  ),
  useTemplate: z.boolean().default(true), // LC-05: Use 6-step blueprint by default
});

const moduleFormSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters").max(255, "Title must be less than 255 characters"),
  description: z.string().optional(),
  shortDescription: z.string().max(500, "Short description must be less than 500 characters").optional(),
  estimatedTime: optionalNumber,
  tags: z.string().optional(),
});

type PathwayFormValues = z.infer<typeof pathwayFormSchema>;
type BlockFormValues = z.infer<typeof blockFormSchema>;
type LessonFormValues = z.infer<typeof lessonFormSchema>;
type ModuleFormValues = z.infer<typeof moduleFormSchema>;

export default function Creator() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("content");
  const [pathwayDialogOpen, setPathwayDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [aiUploadOpen, setAiUploadOpen] = useState(false);
  const { toast } = useToast();

  const isAuthorized = !authLoading && user && String(user.id) === CREATOR_ALLOWED_USER_ID;

  const { data: orgsData } = useQuery({
    queryKey: ["/api/organizations", user?.id, "user-organizations"],
    queryFn: () => organizationsApi.getUserOrganizations(user!.id),
    enabled: !!user?.id && isAuthorized,
    staleTime: 120_000,
  });

  const creatorOrgId: string | null = (orgsData?.data?.[0]?.id as string) ?? null;

  const { data: content, isLoading, isError: isContentError, error: contentError, refetch } = useQuery<CreatedContent>({
    queryKey: ["/api/creator/content"],
    enabled: isAuthorized,
    retry: false,
  });

  const createPathwayMutation = useMutation({
    mutationFn: async (data: PathwayFormValues) => {
      const tags = data.tags ? data.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
      return apiRequest("/api/creator/pathways", {
        method: "POST",
        body: JSON.stringify({
          title: data.title,
          description: data.description || null,
          shortDescription: data.shortDescription || null,
          type: data.type,
          difficulty: data.difficulty,
          estimatedTotalTime: data.estimatedTotalTime || null,
          tags,
        }),
      });
    },
    onSuccess: () => {
      toast({ title: "Pathway created", description: "Your new learning pathway has been created successfully." });
      setPathwayDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/creator/content"] });
    },
    onError: (error: Error) => {
      toast({ title: "Pathway creation failed", description: error.message || "Please try again", variant: "destructive", duration: 15000 });
    },
  });

  const createBlockMutation = useMutation({
    mutationFn: async (data: BlockFormValues) => {
      const tags = data.tags ? data.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
      return apiRequest("/api/creator/blocks", {
        method: "POST",
        body: JSON.stringify({
          title: data.title,
          description: data.description || null,
          shortDescription: data.shortDescription || null,
          topic: data.topic || null,
          difficulty: data.difficulty,
          estimatedTime: data.estimatedTime || null,
          tags,
        }),
      });
    },
    onSuccess: () => {
      toast({ title: "Block created", description: "Your new learning block has been created successfully." });
      setBlockDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/creator/content"] });
    },
    onError: (error: Error) => {
      toast({ title: "Block creation failed", description: error.message || "Please try again", variant: "destructive", duration: 15000 });
    },
  });

  const createLessonMutation = useMutation({
    mutationFn: async (data: LessonFormValues) => {
      return apiRequest("/api/creator/lessons", {
        method: "POST",
        body: JSON.stringify({
          title: data.title,
          shortSummary: data.shortSummary || null,
          detailedDescription: data.detailedDescription || null,
          difficulty: data.difficulty,
          estimatedTime: data.estimatedTime || 10,
          useTemplate: data.useTemplate ?? true, // LC-05: Use 6-step blueprint by default
        }),
      });
    },
    onSuccess: () => {
      toast({ title: "Lesson created", description: "Your new lesson has been created successfully." });
      setLessonDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/creator/content"] });
    },
    onError: (error: Error) => {
      toast({ title: "Lesson creation failed", description: error.message || "Please try again", variant: "destructive", duration: 15000 });
    },
  });

  const createModuleMutation = useMutation({
    mutationFn: async (data: ModuleFormValues) => {
      const tags = data.tags ? data.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
      return apiRequest("/api/creator/modules", {
        method: "POST",
        body: JSON.stringify({
          title: data.title,
          description: data.description || null,
          shortDescription: data.shortDescription || null,
          estimatedTime: data.estimatedTime || null,
          tags,
        }),
      });
    },
    onSuccess: () => {
      toast({ title: "Module created", description: "Your new learning module has been created successfully." });
      setModuleDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/creator/content"] });
    },
    onError: (error: Error) => {
      toast({ title: "Module creation failed", description: error.message || "Please try again", variant: "destructive", duration: 15000 });
    },
  });

  const pathwayForm = useForm<PathwayFormValues>({
    resolver: zodResolver(pathwayFormSchema),
    defaultValues: {
      title: "",
      description: "",
      shortDescription: "",
      type: "skill",
      difficulty: "beginner",
      estimatedTotalTime: undefined,
      tags: "",
    },
  });

  const blockForm = useForm<BlockFormValues>({
    resolver: zodResolver(blockFormSchema),
    defaultValues: {
      title: "",
      description: "",
      shortDescription: "",
      topic: "",
      difficulty: "beginner",
      estimatedTime: undefined,
      tags: "",
    },
  });

  const lessonForm = useForm<LessonFormValues>({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: {
      title: "",
      shortSummary: "",
      detailedDescription: "",
      difficulty: "beginner",
      estimatedTime: 10,
      useTemplate: true, // LC-05: Use 6-step blueprint by default
    },
  });

  const moduleForm = useForm<ModuleFormValues>({
    resolver: zodResolver(moduleFormSchema),
    defaultValues: {
      title: "",
      description: "",
      shortDescription: "",
      estimatedTime: undefined,
      tags: "",
    },
  });

  const onPathwaySubmit = (data: PathwayFormValues) => {
    createPathwayMutation.mutate(data);
  };

  const onBlockSubmit = (data: BlockFormValues) => {
    createBlockMutation.mutate(data);
  };

  const onLessonSubmit = (data: LessonFormValues) => {
    createLessonMutation.mutate(data);
  };

  const onModuleSubmit = (data: ModuleFormValues) => {
    createModuleMutation.mutate(data);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "intermediate": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "advanced": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "expert": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft": return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
      case "published": return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200";
      case "review": return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200";
      default: return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  if (authLoading) {
    return (
      <div className="flex flex-col h-screen bg-background items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex flex-col h-screen bg-background items-center justify-center p-6">
        <SEO noIndex title="Access Denied" description="Creator Studio is restricted to authorized content creators." />
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <ShieldX className="w-16 h-16 text-destructive" />
            </div>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You do not have permission to access Creator Mode. This feature is restricted to authorized content creators only.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" asChild>
              <a href="/app" data-testid="link-back-to-app">Return to App</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isContentError) {
    return (
      <div className="flex flex-col h-screen bg-background items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <ShieldX className="w-16 h-16 text-destructive" />
            </div>
            <CardTitle>Failed to Load Content</CardTitle>
            <CardDescription>
              {contentError instanceof Error ? contentError.message : "Unable to load creator content. You may not have the required permissions."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => refetch()} data-testid="button-retry-content">
              Try Again
            </Button>
            <Button variant="outline" asChild>
              <a href="/app" data-testid="link-back-to-app-error">Return to App</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      <SEO
        title="Creator Studio"
        description="Build and publish learning pathways, blocks, and lessons with BrainWrk's AI-powered creator tools."
        canonicalUrl="/creator"
      />
      <header className="flex items-center justify-between gap-4 px-6 py-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-semibold" data-testid="text-creator-title">Creator Mode</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <a href="/app" data-testid="link-back-to-chat">Back to Chat</a>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" data-testid="button-new-dropdown">
                <Plus className="h-4 w-4 mr-1.5" />
                New
                <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {AI_UPLOAD_ENABLED && (
                <DropdownMenuItem
                  onClick={() => {
                    trackAiUpload('ai_upload.entry_clicked', { orgId: creatorOrgId || undefined });
                    navigate('/creator/ai-upload');
                  }}
                  className="cursor-pointer"
                  data-testid="menu-item-ai-upload"
                >
                  <Sparkles className="h-4 w-4 mr-2 text-primary" />
                  <div>
                    <div className="text-sm font-medium">Bulk AI-Assisted Upload</div>
                    <div className="text-xs text-muted-foreground">Beta</div>
                  </div>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <UserMenu />
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
              <TabsTrigger value="organization" data-testid="tab-organization">Organization</TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="mt-6">
              <div className="grid gap-6">
                {AI_UPLOAD_ENABLED && (
                  <Card className="border-primary/20 bg-primary/5 dark:bg-primary/10" data-testid="card-ai-upload">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Bulk AI-Assisted Upload
                      </CardTitle>
                      <CardDescription>
                        Upload PDFs or images and let AI generate a structured draft for you to review and publish.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Button
                        onClick={() => {
                          trackAiUpload('ai_upload.entry_clicked', { orgId: creatorOrgId || undefined });
                          navigate('/creator/ai-upload');
                        }}
                        className="w-full"
                        size="lg"
                        data-testid="button-open-ai-upload"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Start AI upload
                      </Button>
                      <AIUploadJobList />
                    </CardContent>
                  </Card>
                )}

                <AIUploadWizard
                  open={aiUploadOpen}
                  onClose={() => setAiUploadOpen(false)}
                  creatorOrgId={creatorOrgId}
                />

                <Card>
                  <CardHeader>
                    <CardTitle>Create Learning Content</CardTitle>
                    <CardDescription>Design and publish learning pathways, blocks, modules, and lessons</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Dialog open={pathwayDialogOpen} onOpenChange={setPathwayDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full" size="lg" data-testid="button-create-pathway">
                          <Plus className="w-4 h-4 mr-2" />
                          Create New Pathway
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Map className="w-5 h-5" />
                            Create Learning Pathway
                          </DialogTitle>
                          <DialogDescription>
                            A pathway is a structured learning journey containing multiple blocks and lessons.
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...pathwayForm}>
                          <form onSubmit={pathwayForm.handleSubmit(onPathwaySubmit)} className="space-y-4">
                            <FormField
                              control={pathwayForm.control}
                              name="title"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Title *</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Leadership Fundamentals" {...field} data-testid="input-pathway-title" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={pathwayForm.control}
                              name="shortDescription"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Short Description</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Brief summary (max 500 characters)" {...field} data-testid="input-pathway-short-desc" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={pathwayForm.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Full Description</FormLabel>
                                  <FormControl>
                                    <Textarea placeholder="Detailed description of this learning pathway..." {...field} data-testid="input-pathway-description" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={pathwayForm.control}
                                name="type"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Type</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                      <FormControl>
                                        <SelectTrigger data-testid="select-pathway-type">
                                          <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="skill">Skill</SelectItem>
                                        <SelectItem value="job_role">Job Role</SelectItem>
                                        <SelectItem value="subject">Subject</SelectItem>
                                        <SelectItem value="quality">Quality</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={pathwayForm.control}
                                name="difficulty"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Difficulty</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                      <FormControl>
                                        <SelectTrigger data-testid="select-pathway-difficulty">
                                          <SelectValue placeholder="Select difficulty" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="beginner">Beginner</SelectItem>
                                        <SelectItem value="intermediate">Intermediate</SelectItem>
                                        <SelectItem value="advanced">Advanced</SelectItem>
                                        <SelectItem value="expert">Expert</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={pathwayForm.control}
                                name="estimatedTotalTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Estimated Time (minutes)</FormLabel>
                                    <FormControl>
                                      <Input type="number" placeholder="e.g., 120" {...field} data-testid="input-pathway-time" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={pathwayForm.control}
                                name="tags"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Tags</FormLabel>
                                    <FormControl>
                                      <Input placeholder="comma-separated" {...field} data-testid="input-pathway-tags" />
                                    </FormControl>
                                    <FormDescription className="text-xs">Separate with commas</FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <DialogFooter className="gap-2">
                              <DialogClose asChild>
                                <Button type="button" variant="outline" data-testid="button-cancel-pathway">Cancel</Button>
                              </DialogClose>
                              <Button type="submit" disabled={createPathwayMutation.isPending} data-testid="button-submit-pathway">
                                {createPathwayMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Create Pathway
                              </Button>
                            </DialogFooter>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>

                    <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full" variant="outline" size="lg" data-testid="button-create-block">
                          <Plus className="w-4 h-4 mr-2" />
                          Create New Block
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <LayoutGrid className="w-5 h-5" />
                            Create Learning Block
                          </DialogTitle>
                          <DialogDescription>
                            A block is a thematic segment containing related modules and lessons.
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...blockForm}>
                          <form onSubmit={blockForm.handleSubmit(onBlockSubmit)} className="space-y-4">
                            <FormField
                              control={blockForm.control}
                              name="title"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Title *</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Communication Skills" {...field} data-testid="input-block-title" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={blockForm.control}
                              name="topic"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Topic</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Soft Skills" {...field} data-testid="input-block-topic" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={blockForm.control}
                              name="shortDescription"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Short Description</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Brief summary (max 500 characters)" {...field} data-testid="input-block-short-desc" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={blockForm.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Full Description</FormLabel>
                                  <FormControl>
                                    <Textarea placeholder="Detailed description of this learning block..." {...field} data-testid="input-block-description" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={blockForm.control}
                                name="difficulty"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Difficulty</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                      <FormControl>
                                        <SelectTrigger data-testid="select-block-difficulty">
                                          <SelectValue placeholder="Select difficulty" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="beginner">Beginner</SelectItem>
                                        <SelectItem value="intermediate">Intermediate</SelectItem>
                                        <SelectItem value="advanced">Advanced</SelectItem>
                                        <SelectItem value="expert">Expert</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={blockForm.control}
                                name="estimatedTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Estimated Time (minutes)</FormLabel>
                                    <FormControl>
                                      <Input type="number" placeholder="e.g., 45" {...field} data-testid="input-block-time" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <FormField
                              control={blockForm.control}
                              name="tags"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Tags</FormLabel>
                                  <FormControl>
                                    <Input placeholder="comma-separated tags" {...field} data-testid="input-block-tags" />
                                  </FormControl>
                                  <FormDescription className="text-xs">Separate with commas</FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <DialogFooter className="gap-2">
                              <DialogClose asChild>
                                <Button type="button" variant="outline" data-testid="button-cancel-block">Cancel</Button>
                              </DialogClose>
                              <Button type="submit" disabled={createBlockMutation.isPending} data-testid="button-submit-block">
                                {createBlockMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Create Block
                              </Button>
                            </DialogFooter>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>

                    <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full" variant="outline" size="lg" data-testid="button-create-lesson">
                          <Plus className="w-4 h-4 mr-2" />
                          Create New Lesson
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Create Lesson
                          </DialogTitle>
                          <DialogDescription>
                            A lesson is a 5-15 minute micro-learning unit with steps and interactions.
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...lessonForm}>
                          <form onSubmit={lessonForm.handleSubmit(onLessonSubmit)} className="space-y-4">
                            <FormField
                              control={lessonForm.control}
                              name="title"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Title *</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Introduction to Active Listening" {...field} data-testid="input-lesson-title" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={lessonForm.control}
                              name="shortSummary"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Summary</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Brief summary of the lesson (max 500 characters)" {...field} data-testid="input-lesson-summary" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={lessonForm.control}
                              name="detailedDescription"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Detailed Description</FormLabel>
                                  <FormControl>
                                    <Textarea placeholder="What will learners achieve in this lesson?" {...field} data-testid="input-lesson-description" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={lessonForm.control}
                                name="difficulty"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Difficulty</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                      <FormControl>
                                        <SelectTrigger data-testid="select-lesson-difficulty">
                                          <SelectValue placeholder="Select difficulty" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="beginner">Beginner</SelectItem>
                                        <SelectItem value="intermediate">Intermediate</SelectItem>
                                        <SelectItem value="advanced">Advanced</SelectItem>
                                        <SelectItem value="expert">Expert</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={lessonForm.control}
                                name="estimatedTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Estimated Time (minutes)</FormLabel>
                                    <FormControl>
                                      <Input type="number" min={1} max={60} {...field} data-testid="input-lesson-time" />
                                    </FormControl>
                                    <FormDescription className="text-xs">1-60 minutes recommended</FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <FormField
                              control={lessonForm.control}
                              name="useTemplate"
                              render={({ field }) => (
                                <FormItem className="flex items-center justify-between rounded-lg border p-3 gap-4">
                                  <div className="space-y-0.5">
                                    <FormLabel>Use Standard Template</FormLabel>
                                    <FormDescription className="text-xs">
                                      Start with 6 structured steps (Intro, Mentor, 2 Concepts, Check, Reflection)
                                    </FormDescription>
                                  </div>
                                  <FormControl>
                                    <Switch
                                      checked={field.value}
                                      onCheckedChange={field.onChange}
                                      data-testid="switch-use-template"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <DialogFooter className="gap-2">
                              <DialogClose asChild>
                                <Button type="button" variant="outline" data-testid="button-cancel-lesson">Cancel</Button>
                              </DialogClose>
                              <Button type="submit" disabled={createLessonMutation.isPending} data-testid="button-submit-lesson">
                                {createLessonMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Create Lesson
                              </Button>
                            </DialogFooter>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>

                    <Dialog open={moduleDialogOpen} onOpenChange={setModuleDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full" variant="outline" size="lg" data-testid="button-create-module">
                          <Plus className="w-4 h-4 mr-2" />
                          Create New Module
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5" />
                            Create Learning Module
                          </DialogTitle>
                          <DialogDescription>
                            A module is a sub-theme container within a block that groups related lessons together.
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...moduleForm}>
                          <form onSubmit={moduleForm.handleSubmit(onModuleSubmit)} className="space-y-4">
                            <FormField
                              control={moduleForm.control}
                              name="title"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Title *</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Active Listening Techniques" {...field} data-testid="input-module-title" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={moduleForm.control}
                              name="shortDescription"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Short Description</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Brief summary (max 500 characters)" {...field} data-testid="input-module-short-desc" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={moduleForm.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Full Description</FormLabel>
                                  <FormControl>
                                    <Textarea placeholder="Detailed description of this learning module..." {...field} data-testid="input-module-description" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={moduleForm.control}
                                name="estimatedTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Estimated Time (minutes)</FormLabel>
                                    <FormControl>
                                      <Input type="number" placeholder="e.g., 30" {...field} data-testid="input-module-time" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={moduleForm.control}
                                name="tags"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Tags</FormLabel>
                                    <FormControl>
                                      <Input placeholder="comma-separated" {...field} data-testid="input-module-tags" />
                                    </FormControl>
                                    <FormDescription className="text-xs">Separate with commas</FormDescription>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <DialogFooter className="gap-2">
                              <DialogClose asChild>
                                <Button type="button" variant="outline" data-testid="button-cancel-module">Cancel</Button>
                              </DialogClose>
                              <Button type="submit" disabled={createModuleMutation.isPending} data-testid="button-submit-module">
                                {createModuleMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Create Module
                              </Button>
                            </DialogFooter>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Your Content</CardTitle>
                    <CardDescription>Manage and view your created learning materials</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {content?.pathways && content.pathways.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                              <Map className="w-4 h-4" />
                              Pathways ({content.pathways.length})
                            </h3>
                            <div className="grid gap-3">
                              {content.pathways.map((pathway) => (
                                <div
                                  key={pathway.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md border bg-card hover-elevate"
                                  data-testid={`card-pathway-${pathway.id}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium truncate">{pathway.title}</h4>
                                    <p className="text-sm text-muted-foreground truncate">{pathway.short_description || pathway.description || "No description"}</p>
                                  </div>
                                  <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0 flex-wrap">
                                    <Badge variant="secondary" className={getDifficultyColor(pathway.difficulty)}>
                                      {pathway.difficulty}
                                    </Badge>
                                    <Badge variant="outline" className={getStatusColor(pathway.status)}>
                                      {pathway.status}
                                    </Badge>
                                    {pathway.estimated_total_time && (
                                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {pathway.estimated_total_time}m
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {content?.blocks && content.blocks.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                              <LayoutGrid className="w-4 h-4" />
                              Blocks ({content.blocks.length})
                            </h3>
                            <div className="grid gap-3">
                              {content.blocks.map((block) => (
                                <div
                                  key={block.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md border bg-card hover-elevate"
                                  data-testid={`card-block-${block.id}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium truncate">{block.title}</h4>
                                    <p className="text-sm text-muted-foreground truncate">{block.short_description || block.description || "No description"}</p>
                                  </div>
                                  <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0 flex-wrap">
                                    {block.topic && (
                                      <Badge variant="outline">
                                        <Tag className="w-3 h-3 mr-1" />
                                        {block.topic}
                                      </Badge>
                                    )}
                                    <Badge variant="secondary" className={getDifficultyColor(block.difficulty)}>
                                      {block.difficulty}
                                    </Badge>
                                    <Badge variant="outline" className={getStatusColor(block.status)}>
                                      {block.status}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {content?.modules && content.modules.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                              <Layers className="w-4 h-4" />
                              Modules ({content.modules.length})
                            </h3>
                            <div className="grid gap-3">
                              {content.modules.map((module) => (
                                <div
                                  key={module.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md border bg-card hover-elevate"
                                  data-testid={`card-module-${module.id}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium truncate">{module.title}</h4>
                                    <p className="text-sm text-muted-foreground truncate">{module.short_description || module.description || "No description"}</p>
                                  </div>
                                  <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0 flex-wrap">
                                    {module.tags && module.tags.length > 0 && (
                                      <Badge variant="outline">
                                        <Tag className="w-3 h-3 mr-1" />
                                        {module.tags[0]}
                                        {module.tags.length > 1 && ` +${module.tags.length - 1}`}
                                      </Badge>
                                    )}
                                    <Badge variant="outline" className={getStatusColor(module.status)}>
                                      {module.status}
                                    </Badge>
                                    {module.estimated_time && (
                                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {module.estimated_time}m
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {content?.lessons && content.lessons.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Lessons ({content.lessons.length})
                            </h3>
                            <div className="grid gap-3">
                              {content.lessons.map((lesson) => (
                                <div
                                  key={lesson.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md border bg-card hover-elevate"
                                  data-testid={`card-lesson-${lesson.id}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium truncate">{lesson.title}</h4>
                                    <p className="text-sm text-muted-foreground truncate">{lesson.short_summary || lesson.detailed_description || "No description"}</p>
                                  </div>
                                  <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0 flex-wrap">
                                    <Badge variant="secondary" className={getDifficultyColor(lesson.difficulty)}>
                                      {lesson.difficulty}
                                    </Badge>
                                    <Badge variant="outline" className={getStatusColor(lesson.status)}>
                                      {lesson.status}
                                    </Badge>
                                    {lesson.estimated_time && (
                                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {lesson.estimated_time}m
                                      </span>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/creator/lessons/${lesson.id}`)}
                                      data-testid={`button-edit-lesson-${lesson.id}`}
                                    >
                                      <Pencil className="w-3 h-3 mr-1" />
                                      Edit
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(!content?.pathways?.length && !content?.blocks?.length && !content?.modules?.length && !content?.lessons?.length) && (
                          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="creator-content-empty">
                            <div className="rounded-full bg-muted p-4 mb-4">
                              <BookOpen className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <p className="text-lg font-medium">No content created yet</p>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                              Use the creation buttons above to build your first pathway, block, module, or lesson.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="organization" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Organization Settings</CardTitle>
                  <CardDescription>Manage your organization and team members</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Building2 className="w-12 h-12 text-muted-foreground opacity-50 mb-4" />
                    <p className="text-muted-foreground">Organization management coming soon.</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Creator Settings</CardTitle>
                  <CardDescription>Configure your creator preferences</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Settings className="w-12 h-12 text-muted-foreground opacity-50 mb-4" />
                    <p className="text-muted-foreground">Settings coming soon.</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
